// @flow

import './commons/gilroy.css';
import React, { type Node, Suspense } from 'react';
import { type LookAndFeelRootProps } from '@tectonics/look-and-feel';
import { getThemeId } from '@tectonics/prs/merchant';
import { getMerchant, getPaymentTypeCode } from '@tectonics/prs/incoming-payment';
import { VolcanesThemeProvider, type Theme, getThemeById } from './theme';
import PageLoading from './pages/page-loading/page-loading';
import { getReadingDirectionByIncomingPayment, LEFT_TO_RIGHT } from '../commons/reading-direction';
import { Direction } from './atoms';

const PageCanceledPayment = React.lazy(() => import('./pages/page-canceled-payment'));
const PageConfirmedPayment = React.lazy(() => import('./pages/page-confirmed-payment'));
const PageOpenedPayment = React.lazy(() => import('./pages/page-opened-payment'));
const PagePendingPayment = React.lazy(() => import('./pages/page-pending-payment'));

export default function Volcanes(props: LookAndFeelRootProps): Node {
  return (
    <WithReadingDirection lookAndFeelRootProps={props}>
      <WithGlobalAssets>
        <Page {...props} />
      </WithGlobalAssets>
    </WithReadingDirection>
  );
}

function Page(props: LookAndFeelRootProps): Node {
  if (props.stage === 'loading') {
    return <PageLoading />;
  }
  return <StatusPage {...(props: any)} />;
}

function StatusPage(props: LookAndFeelRootProps): Node {
  return (
    <Suspense fallback={<PageLoading />}>
      <VolcanesThemeProvider theme={getTheme(props)}>
        <StatusPageImpl {...props} />
      </VolcanesThemeProvider>
    </Suspense>
  );
}

function StatusPageImpl(props: LookAndFeelRootProps): Node {
  switch (props.stage) {
    case 'payment-canceled': return <PageCanceledPayment {...props} />;
    case 'payment-confirmed': return <PageConfirmedPayment {...props} />;
    case 'payment-opened': return <PageOpenedPayment {...props} />;
    case 'payment-pending': return <PagePendingPayment {...props} />;
    default: throw new Error(`Volcanes - StatusPageImpl - status "${status}" does not contains a page implementation`);
  }
}

function getTheme(props: LookAndFeelRootProps): Theme {
  const { incomingPayment } = props;
  if (!incomingPayment) {
    return getThemeById('default');
  }

  if (getPaymentTypeCode(incomingPayment) === 'efecty' && incomingPayment.status === 'PE') {
    return getThemeById('new-voucher');
  }

  const themeId = getThemeId(getMerchant(props.incomingPayment));
  return getThemeById(themeId);
}

type LoadAssetsProps = {|
  children: Node,
|};

function WithGlobalAssets({ children }: LoadAssetsProps): Node {
  return (
    <>
      <link
        href="https://fonts.googleapis.com/css?family=Open+Sans:300,400,600,700"
        rel="stylesheet"
      />
      {children}
    </>
  );
}

type WithReadingDirectionProps = {|
  children: Node,
  lookAndFeelRootProps: LookAndFeelRootProps,
|};

function WithReadingDirection({ children, lookAndFeelRootProps }: WithReadingDirectionProps): Node {
  const { incomingPayment } = lookAndFeelRootProps;
  const readingDirection = getReadingDirectionByIncomingPayment(incomingPayment);

  return (
    <Direction dir={readingDirection === LEFT_TO_RIGHT ? 'ltr' : 'rtl'}>
      {children}
    </Direction>
  );
}
